// Parse an imported blog article (Markdown or HTML, e.g. exported from Marblism)
// into the fields the blog editor needs: title, content HTML, cover image and
// excerpt. Markdown is the recommended format — it's the cleanest to author and
// its images come through as plain URLs — but raw HTML is accepted too.
import { marked } from "marked";
import DOMPurify from "dompurify";

// Tags/attributes we want to keep from imported content.
const SANITIZE_OPTS = {
  ALLOWED_TAGS: [
    "h1", "h2", "h3", "h4", "h5", "h6",
    "p", "a", "ul", "ol", "li", "blockquote", "pre", "code",
    "strong", "em", "b", "i", "u", "s", "br", "hr",
    "img", "figure", "figcaption", "table", "thead", "tbody", "tr", "th", "td",
    "span", "div",
  ],
  ALLOWED_ATTR: ["href", "src", "alt", "title", "target", "rel", "width", "height"],
  ADD_ATTR: ["target"],
};

// Heuristic: does this text look like HTML rather than Markdown?
function looksLikeHtml(text, fileName = "") {
  if (/\.html?$/i.test(fileName)) return true;
  if (/\.(md|markdown|txt)$/i.test(fileName)) return false;
  // No extension hint — sniff the content. Real HTML opens with a block tag.
  return /<\s*(h[1-6]|p|div|article|section|html|body|img)\b/i.test(text);
}

function stripFirstMatch(html, regex) {
  let removed = "";
  const next = html.replace(regex, (m) => {
    if (removed) return m; // only the first
    removed = m;
    return "";
  });
  return { html: next, removed };
}

function textContent(html) {
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || "").replace(/\s+/g, " ").trim();
}

function getAttr(tagHtml, attr) {
  const el = document.createElement("div");
  el.innerHTML = tagHtml;
  const node = el.firstElementChild;
  return node ? node.getAttribute(attr) || "" : "";
}

/**
 * @param {string} raw   - the pasted/dropped Markdown or HTML
 * @param {string} fileName - optional file name, used to detect the format
 * @returns {{title, contentHtml, coverImageUrl, excerpt, format}}
 */
export function parseImportedArticle(raw, fileName = "") {
  const text = (raw || "").trim();
  if (!text) {
    return { title: "", contentHtml: "", coverImageUrl: "", excerpt: "", format: "empty" };
  }

  const isHtml = looksLikeHtml(text, fileName);
  let html = isHtml ? text : marked.parse(text, { breaks: false, gfm: true });

  // Sanitize before we do anything else.
  html = DOMPurify.sanitize(html, SANITIZE_OPTS);

  // Title = first <h1>; remove it from the body so it isn't duplicated under
  // the title that the page renders separately.
  const h1 = stripFirstMatch(html, /<h1\b[^>]*>[\s\S]*?<\/h1>/i);
  const title = h1.removed ? textContent(h1.removed) : "";
  html = h1.html;

  // Cover image = first <img>; lift it out of the body so it shows as the hero
  // image once (the public page renders the cover separately at the top).
  const img = stripFirstMatch(html, /<img\b[^>]*>/i);
  const coverImageUrl = img.removed ? getAttr(img.removed, "src") : "";
  html = img.html;

  // Tidy up: drop empty paragraphs left behind (e.g. where the hero image sat)
  // and collapse a leading run of blank lines.
  const contentHtml = html
    .replace(/<p>\s*(?:<br\s*\/?>)?\s*<\/p>/gi, "")
    .replace(/^\s+/, "")
    .trim();

  // Excerpt = the first real paragraph of text, trimmed to a sane length.
  const firstP = contentHtml.match(/<p\b[^>]*>([\s\S]*?)<\/p>/i);
  let excerpt = firstP ? textContent(firstP[0]) : textContent(contentHtml);
  if (excerpt.length > 200) excerpt = `${excerpt.slice(0, 197).trimEnd()}...`;

  return {
    title,
    contentHtml,
    coverImageUrl,
    excerpt,
    format: isHtml ? "html" : "markdown",
  };
}
